import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { PlayArrow, Stop, Search as SearchIcon, ContentCopy as ContentCopyIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiClient, { generateVoiceSample } from '../../../../../api/apiClient';

export interface VoiceModel {
  id: string;
  name: string;
  provider?: string | null;
  category?: string | null;
  labels?: string[] | null;
  description?: string | null;
  gender?: string | null;
  accent?: string | null;
  age?: string | null; // e.g., child | teen | young adult | adult | senior
  // Optional alternate ids
  voiceId?: string;
  kvoiceId?: string;
}

export interface VoiceLibraryDialogProps {
  open: boolean;
  websitePK: string;
  voiceSampleText?: string;
  onClose: () => void;
  onSelectVoice: (voice: VoiceModel) => void;
}

// Helper to try multiple endpoints to fetch voices (to accommodate different backends)
const createListVoices = () => {
  // Candidate endpoints to try in order
  const candidates = [
    '/publisher/voice-models',
    '/publisher/voices',
    '/voices',
  ];
  let workingEndpoint: string | null = null;

  type ListResponse = { items: any[]; total: number; page?: number; pageSize?: number } | any[];

  const normalizeVoice = (raw: any): VoiceModel => {
    const idFromPk = typeof raw?.PK === 'string' && raw.PK.startsWith('VOICE_MODEL#') ? raw.PK.split('#')[1] : undefined;
    const vid = raw?.kvoiceId || raw?.voiceId || raw?.voice_id || raw?.id || idFromPk || '';
    return {
      id: vid || raw?.id || String(raw?.name || ''),
      name: raw?.name || raw?.title || 'Unknown',
      provider: raw?.provider || raw?.source || null,
      category: raw?.category || null,
      labels: Array.isArray(raw?.labels) ? raw.labels : (Array.isArray(raw?.tags) ? raw.tags : null),
      description: raw?.description || raw?.descriptive || null,
      gender: raw?.gender || null,
      accent: raw?.accent || null,
      age: raw?.age || null,
      voiceId: raw?.voiceId || raw?.voice_id || undefined,
      kvoiceId: raw?.kvoiceId || undefined,
    };
  };

  const list = async (params: { search?: string; page?: number; pageSize?: number; gender?: string; age?: string; accent?: string }): Promise<{ items: VoiceModel[]; total: number }> => {
    const query = {
      q: params.search || '',
      search: params.search || '',
      page: params.page || 1,
      pageSize: params.pageSize || 10,
      limit: params.pageSize || 10,
      gender: params.gender || undefined,
      age: params.age || undefined,
      accent: params.accent || undefined,
    } as any;

    const tryEndpoint = async (endpoint: string): Promise<{ items: VoiceModel[]; total: number } | null> => {
      try {
        const res = await apiClient.get(endpoint, { params: query });
        const data: ListResponse = res.data;
        if (Array.isArray(data)) {
          const items = (data as any[]).map(normalizeVoice);
          return { items, total: items.length };
        }
        const rawItems = Array.isArray((data as any).items) ? (data as any).items as any[] : [];
        const items = rawItems.map(normalizeVoice);
        const total = typeof (data as any).total === 'number' ? (data as any).total : items.length;
        return { items, total };
      } catch (e) {
        return null;
      }
    };

    if (workingEndpoint) {
      const ok = await tryEndpoint(workingEndpoint);
      if (ok) return ok;
      // fallback to probe again
      workingEndpoint = null;
    }

    for (const ep of candidates) {
      const ok = await tryEndpoint(ep);
      if (ok) {
        workingEndpoint = ep;
        return ok;
      }
    }

    // Nothing worked
    throw new Error('Unable to load voices. No compatible endpoint responded.');
  };

  return list;
};

const listVoices = createListVoices();

const VoiceLibraryDialog: React.FC<VoiceLibraryDialogProps> = ({ open, websitePK, voiceSampleText, onClose, onSelectVoice }) => {
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState<string>('');
  const [age, setAge] = useState<string>('');
  const [accent, setAccent] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [voices, setVoices] = useState<VoiceModel[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // audio playback per row
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isGeneratingSample, setIsGeneratingSample] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  // Debounced query params for search/filters
  const [debouncedQuery, setDebouncedQuery] = useState<{ search: string; gender: string; age: string; accent: string }>({ search: '', gender: '', age: '', accent: '' });
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery({ search, gender, age, accent });
    }, 300);
    return () => clearTimeout(t);
  }, [search, gender, age, accent]);

  // Reset to first page whenever search/filters change (after debounce)
  useEffect(() => {
    if (page !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Fetch voices whenever dialog is open and deps change
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listVoices({
          search: debouncedQuery.search,
          page,
          pageSize,
          gender: debouncedQuery.gender,
          age: debouncedQuery.age,
          accent: debouncedQuery.accent,
        });
        let items = res.items as VoiceModel[];

        // Client-side filtering fallback for gender/age/accent if server doesn't support
        const norm = (s?: string | null) => (s || '').toString().toLowerCase();
        const g = norm(debouncedQuery.gender); const a = norm(debouncedQuery.age); const ac = norm(debouncedQuery.accent);
        if (g) items = items.filter(v => norm(v.gender).includes(g));
        if (a) items = items.filter(v => norm(v.age).includes(a));
        if (ac) items = items.filter(v => norm(v.accent).includes(ac));

        // Client-side pagination fallback if server ignored page/pageSize
        const totalCount = (typeof res.total === 'number' && res.total >= items.length) ? res.total : items.length;
        const serverLikelyIgnoredPaging = items.length > pageSize && (totalCount === items.length);
        const sliced = serverLikelyIgnoredPaging ? items.slice((page - 1) * pageSize, page * pageSize) : items;

        if (!cancelled) {
          setVoices(sliced);
          setTotal(serverLikelyIgnoredPaging ? items.length : totalCount);
        }
      } catch (e: any) {
        if (!cancelled) {
          setVoices([]);
          setTotal(0);
          setError(e?.message || 'Failed to load voices');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [open, page, pageSize, debouncedQuery, refreshTick]);

  useEffect(() => {
    return () => {
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, []);

  const resolveVoiceId = (v: VoiceModel) => v.kvoiceId || v.voiceId || v.id || '';

  const handlePlay = async (v: VoiceModel) => {
    if (playingId === resolveVoiceId(v)) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    if (!voiceSampleText || !voiceSampleText.trim()) {
      alert('Enter voice sample text first, then try again.');
      return;
    }
    const vid = resolveVoiceId(v);
    if (!vid) {
      alert('Selected voice has no ID to play.');
      return;
    }
    setIsGeneratingSample(true);
    try {
      const res = await generateVoiceSample(websitePK, vid, voiceSampleText);
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      const url = URL.createObjectURL(res.audio);
      audioUrlRef.current = url;
      setPlayingId(vid);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {/* ignored */});
        }
      }, 0);
    } catch (e: any) {
      console.error('Failed to play sample:', e);
      alert(e?.response?.data?.error || e?.message || 'Failed to generate voice sample');
    } finally {
      setIsGeneratingSample(false);
    }
  };

  const handleAudioEnded = () => {
    setPlayingId(null);
  };

  const copyId = async (id: string) => {
    try { await navigator.clipboard.writeText(id); } catch {}
  };

  const handleRefresh = () => {
    setRefreshTick(t => t + 1);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Voice Library</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <TextField
            placeholder="Search voices by name, label, provider…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="voice-gender-label">Gender</InputLabel>
            <Select labelId="voice-gender-label" label="Gender" value={gender} onChange={(e) => setGender(e.target.value)}>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="non-binary">Non-binary</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="voice-age-label">Age</InputLabel>
            <Select labelId="voice-age-label" label="Age" value={age} onChange={(e) => setAge(e.target.value)}>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="young">Young</MenuItem>
              <MenuItem value="middle_aged">Middle Aged</MenuItem>
              <MenuItem value="old">Old</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder="Accent (e.g., US, British, Spanish)"
            value={accent}
            onChange={(e) => setAccent(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {error && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Paper>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {/* Removed Voice ID column */}
                <TableCell>Gender</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Accent</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                      <CircularProgress size={20} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : voices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" sx={{ py: 2 }}>No voices found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                voices.map(v => {
                  const vid = resolveVoiceId(v);
                  return (
                    <TableRow key={vid || v.name} hover>
                      <TableCell>
                        <Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title={vid ? 'Click to copy Voice ID' : ''}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{ cursor: vid ? 'pointer' : 'default', textDecoration: vid ? 'underline' : 'none' }}
                                onClick={() => { if (vid) copyId(vid); }}
                              >
                                {v.name}
                              </Typography>
                            </Tooltip>
                            {vid && <ContentCopyIcon fontSize="inherit" sx={{ color: 'text.secondary', opacity: 0.6 }} />}
                          </Stack>
                          {!!v.labels?.length && (
                            <Typography variant="caption" color="text.secondary">{v.labels.join(', ')}</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      {/* Removed Voice ID cell */}
                      <TableCell>{v.gender || '—'}</TableCell>
                      <TableCell>{v.age || '—'}</TableCell>
                      <TableCell>{v.accent || '—'}</TableCell>
                      <TableCell>{v.provider || '—'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          <Tooltip title={playingId === vid ? 'Stop' : 'Play sample'}>
                            <span>
                              <IconButton onClick={() => handlePlay(v)} disabled={isGeneratingSample || !vid} color={playingId === vid ? 'error' : 'primary'}>
                                {playingId === vid ? <Stop /> : <PlayArrow />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => onSelectVoice({ ...v, id: vid })}
                            sx={{
                              whiteSpace: 'nowrap',
                              textTransform: 'none',
                              minHeight: 30,
                              lineHeight: '30px',
                              px: 1.25,
                            }}
                          >
                            Use Voice
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">{total} total</Typography>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" size="small" />
        </Stack>

        {/* Hidden audio for previews */}
        <audio ref={audioRef} src={audioUrlRef.current || undefined} onEnded={handleAudioEnded} style={{ display: 'none' }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoiceLibraryDialog;